/**
 * The Corporation screen's and the founding flow's rules (M6 plan P33, spec §7.1, §8). Pure: the panels render
 * these and send the commands; the worker (`strategic/corp/corp.ts foundCorp`, `money.ts companyTransfer`) stays the
 * judge, and these mirror its refusals so the form explains them before sending.
 */
import { z } from 'zod'
import corpJson from '../../data/strategic/corp.json'
import type { CorpView, LiveryColors, MilestoneName, PnlLine } from '../../strategic/types'
import { formatCr } from '../format'
import screenJson from './corpScreen.json'

/** The founding rules from corp.json (`founding`, `tickers`, `transfers`). */
export interface FoundingRules {
  fee: number
  requiresOwnership: boolean
  tickerLength: readonly [number, number]
  /** The AI factions' tickers, upper case. */
  taken: readonly string[]
  minTransfer: number
}

const rulesShape = z.object({
  founding: z.object({
    fee: z.number().nonnegative(),
    requiresOwnership: z.boolean(),
    tickerLength: z.tuple([z.number().int(), z.number().int()]),
  }),
  tickers: z.record(z.string(), z.string()),
  transfers: z.object({ minAmount: z.number().nonnegative() }),
})

let rules: FoundingRules | null = null

/** The bundled rules (parsed once). */
export function foundingRules(): FoundingRules {
  if (rules !== null) return rules
  const r = rulesShape.parse(corpJson)
  rules = {
    fee: r.founding.fee,
    requiresOwnership: r.founding.requiresOwnership,
    tickerLength: r.founding.tickerLength,
    taken: Object.values(r.tickers).map((t) => t.toUpperCase()),
    minTransfer: r.transfers.minAmount,
  }
  return rules
}

/** What the founding form holds. */
export interface FoundingInput {
  founded: boolean
  leaseOwned: boolean
  wallet: number
  name: string
  ticker: string
}

export type FoundingBlock = 'alreadyFounded' | 'noOwnership' | 'noName' | 'tickerLength' | 'tickerLetters' | 'tickerTaken' | 'noFunds'

export interface FoundingModel {
  canFound: boolean
  /** The first reason founding is refused, in the worker's order; null when it may go ahead. */
  block: FoundingBlock | null
  reason: string
  feeLabel: string
  /** The ticker as it will be registered (upper case, trimmed). */
  ticker: string
}

const BLOCK_TEXT: Readonly<Record<FoundingBlock, string>> = {
  alreadyFounded: 'Your corporation is already registered.',
  noOwnership: 'Registration opens once you own your ship’s lease outright.',
  noName: 'Give the corporation a name.',
  tickerLength: 'The ticker is {min}–{max} letters.',
  tickerLetters: 'The ticker uses the letters A–Z only.',
  tickerTaken: 'That ticker belongs to another faction.',
  noFunds: 'The registration fee is {fee}; your wallet is short.',
}

/** Upper case, trimmed: the form shows and sends the ticker like this. */
export function normalTicker(ticker: string): string {
  return ticker.trim().toUpperCase()
}

/** The founding form's state: the worker's refusals in its order (`foundCorp`). */
export function foundingModel(input: FoundingInput, r: FoundingRules = foundingRules()): FoundingModel {
  const ticker = normalTicker(input.ticker)
  const [min, max] = r.tickerLength
  let block: FoundingBlock | null = null
  if (input.founded) block = 'alreadyFounded'
  else if (r.requiresOwnership && !input.leaseOwned) block = 'noOwnership'
  else if (input.name.trim() === '') block = 'noName'
  else if (ticker.length < min || ticker.length > max) block = 'tickerLength'
  else if (!/^[A-Z]+$/.test(ticker)) block = 'tickerLetters'
  else if (r.taken.includes(ticker)) block = 'tickerTaken'
  else if (!(input.wallet >= r.fee)) block = 'noFunds'
  const reason = block === null ? '' : BLOCK_TEXT[block].replace('{min}', String(min)).replace('{max}', String(max)).replace('{fee}', formatCr(r.fee))
  return {
    canFound: block === null,
    block,
    reason,
    feeLabel: formatCr(r.fee),
    ticker,
  }
}

// ---------------------------------------------------------------------------------------------
// P&L
// ---------------------------------------------------------------------------------------------

export interface PnlRow {
  key: string
  label: string
  yesterday: number
  today: number
}

export interface PnlModel {
  income: PnlRow[]
  costs: PnlRow[]
  totals: { income: PnlRow; costs: PnlRow; net: PnlRow }
}

function total(key: string, label: string, lines: readonly PnlLine[]): PnlRow {
  let y = 0
  let t = 0
  for (const l of lines) {
    y += l.yesterday
    t += l.today
  }
  return { key, label, yesterday: y, today: t }
}

/** The worker's P&L line keys (`strategic/view/corpView.ts`): `pst-<seq>` a station, `other` the adjustments line. */
const STATION_KEY_PREFIX = 'pst-'
const OTHER_KEY = 'other'

/** An income line's group (spec §8 "income by ship and station"): ships, then stations, then other income last. */
function incomeGroup(l: Pick<PnlRow, 'key'>): number {
  if (l.key === OTHER_KEY) return 2
  return l.key.startsWith(STATION_KEY_PREFIX) ? 1 : 0
}

/**
 * The P&L table (spec §8: income by ship and station, costs by category): the view's lines, income by group (ships,
 * stations, then *Other income and adjustments* last; D-459 nit) and largest first within a group, costs largest loss
 * first, and totals. The net row is the view's own `net` (the faction day ledger); a line sum that disagrees with it
 * is the ledger's transfers, which are not P&L.
 */
export function pnlModel(view: Pick<CorpView, 'income' | 'costs' | 'net'>): PnlModel {
  const income = view.income
    .map((l) => ({ ...l }))
    .sort((a, b) => incomeGroup(a) - incomeGroup(b) || b.yesterday + b.today - (a.yesterday + a.today) || a.label.localeCompare(b.label))
  const costs = view.costs.map((l) => ({ ...l })).sort((a, b) => a.yesterday + a.today - (b.yesterday + b.today) || a.label.localeCompare(b.label))
  return {
    income,
    costs,
    totals: {
      income: total('income', 'Income', view.income),
      costs: total('costs', 'Costs', view.costs),
      net: {
        key: 'net',
        label: 'Net',
        yesterday: view.net.yesterday,
        today: view.net.today,
      },
    },
  }
}

// ---------------------------------------------------------------------------------------------
// Transfers, milestones, livery
// ---------------------------------------------------------------------------------------------

export type TransferDirection = 'toCompany' | 'toWallet'

/** Why a transfer of `amount` cannot go now (the worker's `companyTransfer` rules), or '' when it can. */
export function transferBlock(
  amount: number,
  direction: TransferDirection,
  wallet: number,
  treasury: number,
  r: FoundingRules = foundingRules(),
): string {
  if (!Number.isFinite(amount) || amount <= 0 || amount < r.minTransfer) return `Transfers start at ${formatCr(r.minTransfer)}.`
  if (direction === 'toCompany' && !(wallet >= amount)) return 'Your wallet is short.'
  if (direction === 'toWallet' && !(treasury >= amount)) return 'The company account is short.'
  return ''
}

const milestoneLabels = z.record(z.string(), z.string()).parse(screenJson.milestones)

/** The player-facing milestone list: label, and the faction day reached (null = not yet). */
export function milestoneRows(view: Pick<CorpView, 'milestones'>): { id: MilestoneName; label: string; day: number | null }[] {
  return view.milestones.map((m) => ({
    id: m.id,
    label: milestoneLabels[m.id] ?? m.id,
    day: m.day,
  }))
}

const HEX = /^#[0-9a-f]{6}$/i

/** True when every livery colour is a #rrggbb hex. */
export function liveryValid(l: LiveryColors): boolean {
  return HEX.test(l.primary) && HEX.test(l.secondary) && HEX.test(l.accent)
}

export interface LiveryPreset extends LiveryColors {
  label: string
}

/** corpScreen.json `liveryPresets`. */
export function liveryPresets(): readonly LiveryPreset[] {
  return screenJson.liveryPresets
}

/** The default livery (corp.json `livery`): the colours before founding. */
export function defaultLivery(): LiveryColors {
  return { ...corpJson.livery }
}

/** The Registration tab's order among the station tabs. */
export const REGISTRATION_ORDER: number = screenJson.order.registration
export const TRANSFER_STEPS: readonly number[] = screenJson.transferSteps
